import { bunja, createScope } from 'bunja';

import type { IntervalDataType } from '@flipster/r230522/original/interval/interval-key';
import type { IntervalData, IntervalDataUpdate } from '@flipster/r230522/original/interval/types';
import { weakR230522SocketBunja } from '@flipster/r230522/original/socket';
import type { WsV2TableEvent } from '@flipster/r230522/original/types';
import { tradeLogger } from '@flipster/utils/datadog';

import type { ResolutionString } from '../../lib/charting_library';
import {
  barTimestamp,
  calcBarCount,
  getBarsUpdater,
  socketIntervalDataToBar,
  socketIntervalDataToTimestamp,
  updateBarWithPartialSocketIntervalData,
} from '../bars';
import { normalizeIntervalDataType } from '../misc/chart-price-type';
import { createCompositeSymbolName, resolutionToSecs } from '../misc/helper';
import TradingView from '../trading-view';

export const TradingViewScope = createScope<TradingView | undefined>();

export const reconnectionLongTermBunja = bunja(() => {
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const tv = bunja.use(TradingViewScope);

  bunja.effect(() => {
    const listener = () => {
      tradeLogger.warn('chart_session_not_reconnected', {
        url: window.location.href,
        symbol: tv?.symbol,
      });
    };

    r230522Socket.on('reconnectionLongTerm', listener);
    return () => {
      r230522Socket.off('reconnectionLongTerm', listener);
    };
  });

  return {};
});
reconnectionLongTermBunja.debugLabel = 'reconnectionLongTermBunja';

export const intervalDataBunja = bunja(() => {
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);

  bunja.effect(() => {
    const listener = (message: WsV2TableEvent<IntervalData | IntervalDataUpdate>) => {
      if (!message.data) return;
      Object.entries(message.data).forEach(([action, values]) => {
        Object.entries(values as Record<string, IntervalData | IntervalDataUpdate>).forEach(([key, value]) => {
          const [symbol, priceType, resolutionString] = key.split(':') as [string, IntervalDataType, string];
          // Spot MID is a real source; perp legacy MID aliases LAST. Match bars updater composite key.
          const symbolName = createCompositeSymbolName(symbol, normalizeIntervalDataType(priceType, symbol));
          const resolution = resolutionToSecs(resolutionString as ResolutionString);
          /**
           * NOTE: Should not create bars updater from message handler.
           * or that may occurs memory leak or wrong data comparison by barsUpdater / ticker.
           */
          const barsUpdater = getBarsUpdater(symbolName, Number(resolution));
          if (barsUpdater == null) return;

          const timestamp = socketIntervalDataToTimestamp(value);
          switch (action) {
            case 's':
            case 'i':
              return barsUpdater.tick(timestamp, (lastBar) => {
                if (lastBar != null) {
                  const gap = calcBarCount(
                    { from: barTimestamp(lastBar), to: barTimestamp({ time: timestamp }) },
                    resolution,
                  );
                  if (gap > 1) {
                    tradeLogger.warn('chart_data_missing', {
                      error_message: 'An attempt to insert bars from missing times has been detected',
                      error_category: 'websocket_errors',
                      symbolName,
                      resolution,
                      timestamp,
                      lastBar,
                      gap,
                      url: window.location.href,
                    });
                  }
                }

                return {
                  bar: socketIntervalDataToBar(value),
                  isFinished: Boolean(value.f),
                };
              });
            case 'u':
              return barsUpdater.tick(timestamp, (lastBar) => {
                if (!lastBar) {
                  // 원래는 이런 경우가 없어야 하지만 브릿지 등으로 인해서
                  // 소켓에서 insert 없이 update가 내려오는 경우가 있을 수 있음.
                  // 이 경우에 lastBar가 undefined일 수 있고,
                  // barsUpdater의 #flushTickQueue 에서 이 에러를 받아서 핸들하도록 함.
                  throw new Error('update without insert');
                }
                if (timestamp !== lastBar.time) {
                  const gap = calcBarCount(
                    { from: barTimestamp(lastBar), to: barTimestamp({ time: timestamp }) },
                    resolution,
                  );
                  tradeLogger.warn('chart_data_missing', {
                    error_message: 'An attempt to merge bars from different times has been detected',
                    error_category: 'websocket_errors',
                    symbolName,
                    resolution,
                    timestamp,
                    lastBar,
                    gap,
                    url: window.location.href,
                  });
                }
                const bar = updateBarWithPartialSocketIntervalData(lastBar, value);
                const isFinished = Boolean(value.f);
                return { bar, isFinished };
              });
            default:
          }
        });
      });
    };

    r230522Socket.on('market/interval-data', listener);
    return () => {
      r230522Socket.off('market/interval-data', listener);
    };
  });

  return {};
});
intervalDataBunja.debugLabel = 'intervalDataBunja';
